import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Heartbeat, ShieldCheck, ChartLineUp, Users, Brain,
  ArrowRight, CheckCircle, Warning, XCircle, Sparkle,
  ClipboardText, CaretRight, Download, EnvelopeSimple
} from "@phosphor-icons/react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const HealthScorePage = () => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [score, setScore] = useState(null);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [organization, setOrganization] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const questions = [
    {
      id: "survey_readiness",
      category: "Compliance",
      question: "How confident are you in your organization's survey readiness?",
      icon: ShieldCheck,
      options: [
        { value: 4, label: "Very confident - We could pass a survey tomorrow" },
        { value: 3, label: "Somewhat confident - Minor gaps to address" },
        { value: 2, label: "Uncertain - We have known compliance gaps" },
        { value: 1, label: "Concerned - Significant work needed" }
      ]
    },
    {
      id: "qapi_program",
      category: "Quality",
      question: "How would you describe your QAPI program?",
      icon: ChartLineUp,
      options: [
        { value: 4, label: "Robust - Active PIPs with measurable outcomes" },
        { value: 3, label: "Functional - Meeting minimum requirements" },
        { value: 2, label: "Basic - Documentation exists but limited activity" },
        { value: 1, label: "Minimal - Needs significant development" }
      ]
    },
    {
      id: "documentation",
      category: "Clinical",
      question: "How consistently does your documentation support medical necessity?",
      icon: ClipboardText,
      options: [
        { value: 4, label: "Excellent - Comprehensive, timely, and audit-ready" },
        { value: 3, label: "Good - Generally complete with occasional gaps" },
        { value: 2, label: "Fair - Inconsistent quality across staff" },
        { value: 1, label: "Needs improvement - Frequent deficiencies" }
      ]
    },
    {
      id: "idg_effectiveness",
      category: "Operations",
      question: "How effective are your IDG meetings?",
      icon: Users,
      options: [
        { value: 4, label: "Highly effective - Meaningful care coordination" },
        { value: 3, label: "Adequate - Cover required elements" },
        { value: 2, label: "Could improve - Often rushed or incomplete" },
        { value: 1, label: "Problematic - Compliance concerns" }
      ]
    },
    {
      id: "staff_training",
      category: "Workforce",
      question: "How comprehensive is your staff training program?",
      icon: Brain,
      options: [
        { value: 4, label: "Excellent - Ongoing education with competency tracking" },
        { value: 3, label: "Good - Regular training with documentation" },
        { value: 2, label: "Basic - Orientation only, limited ongoing" },
        { value: 1, label: "Minimal - Significant gaps exist" }
      ]
    },
    {
      id: "census_growth",
      category: "Growth",
      question: "How would you describe your census trend over the past 6 months?",
      icon: ChartLineUp,
      options: [
        { value: 4, label: "Growing steadily - Exceeding targets" },
        { value: 3, label: "Stable - Meeting expectations" },
        { value: 2, label: "Flat - Below where we'd like to be" },
        { value: 1, label: "Declining - Significant concerns" }
      ]
    },
    {
      id: "referral_conversion",
      category: "Growth",
      question: "What is your approximate referral-to-admission conversion rate?",
      icon: Heartbeat,
      options: [
        { value: 4, label: "Above 75%" },
        { value: 3, label: "60-75%" },
        { value: 2, label: "45-60%" },
        { value: 1, label: "Below 45%" }
      ]
    },
    {
      id: "technology",
      category: "Operations",
      question: "How well does technology support your operations?",
      icon: Brain,
      options: [
        { value: 4, label: "Excellent - Integrated systems, real-time data" },
        { value: 3, label: "Good - Adequate tools with some manual processes" },
        { value: 2, label: "Fair - Multiple systems, limited integration" },
        { value: 1, label: "Poor - Largely manual, outdated systems" }
      ]
    },
    {
      id: "leadership",
      category: "Leadership",
      question: "How stable is your leadership team?",
      icon: Users,
      options: [
        { value: 4, label: "Very stable - Experienced team, low turnover" },
        { value: 3, label: "Stable - Some recent changes but solid" },
        { value: 2, label: "Transitioning - Key positions recently filled" },
        { value: 1, label: "Unstable - Multiple vacancies or high turnover" }
      ]
    },
    {
      id: "financial_health",
      category: "Financial",
      question: "How would you rate your organization's financial health?",
      icon: ChartLineUp,
      options: [
        { value: 4, label: "Strong - Healthy margins, adequate reserves" },
        { value: 3, label: "Good - Meeting targets, manageable debt" },
        { value: 2, label: "Challenged - Tight margins, cash flow concerns" },
        { value: 1, label: "Struggling - Financial viability at risk" }
      ]
    }
  ];

  const handleAnswer = (value) => {
    const newAnswers = { ...answers, [questions[currentQuestion].id]: value };
    setAnswers(newAnswers);
    
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      calculateScore(newAnswers);
    }
  };

  const calculateScore = (finalAnswers) => {
    setLoading(true);
    const total = Object.values(finalAnswers).reduce((sum, val) => sum + val, 0);
    const maxScore = questions.length * 4;
    const percentage = Math.round((total / maxScore) * 100);
    
    setTimeout(() => {
      setScore(percentage);
      setShowResults(true);
      setLoading(false);
    }, 1500);
  };

  const getScoreCategory = () => {
    if (score >= 85) return { label: "Excellent", color: "text-green-600", bg: "bg-green-100", icon: CheckCircle };
    if (score >= 70) return { label: "Good", color: "text-blue-600", bg: "bg-blue-100", icon: CheckCircle };
    if (score >= 50) return { label: "Needs Attention", color: "text-yellow-600", bg: "bg-yellow-100", icon: Warning };
    return { label: "At Risk", color: "text-red-600", bg: "bg-red-100", icon: XCircle };
  };

  const getRecommendations = () => {
    const recs = [];
    if (answers.survey_readiness <= 2) recs.push({ area: "Survey Readiness", text: "Schedule a mock survey to identify gaps before your next state visit." });
    if (answers.qapi_program <= 2) recs.push({ area: "QAPI Program", text: "Strengthen your Performance Improvement Projects with measurable outcomes." });
    if (answers.documentation <= 2) recs.push({ area: "Documentation", text: "Implement documentation audits and targeted staff training." });
    if (answers.idg_effectiveness <= 2) recs.push({ area: "IDG Meetings", text: "Restructure IDG meetings for meaningful care coordination." });
    if (answers.staff_training <= 2) recs.push({ area: "Staff Training", text: "Develop a comprehensive competency-based training program." });
    if (answers.census_growth <= 2) recs.push({ area: "Census Growth", text: "Review referral source relationships and conversion processes." });
    if (answers.referral_conversion <= 2) recs.push({ area: "Referral Conversion", text: "Optimize your intake process for faster response times." });
    if (answers.technology <= 2) recs.push({ area: "Technology", text: "Evaluate operational intelligence solutions to improve visibility." });
    if (answers.leadership <= 2) recs.push({ area: "Leadership", text: "Consider executive coaching or interim leadership support." });
    if (answers.financial_health <= 2) recs.push({ area: "Financial Health", text: "Conduct operational efficiency analysis to improve margins." });
    return recs.slice(0, 4);
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/api/health-score`, {
        email,
        organization,
        score,
        answers,
        recommendations: getRecommendations().map(r => r.area)
      });
      setEmailSubmitted(true);
    } catch (err) {
      console.error("Failed to save results:", err);
      setEmailSubmitted(true);
    }
  };

  const currentQ = questions[currentQuestion];
  const Icon = currentQ?.icon;
  const category = getScoreCategory();
  const CategoryIcon = category.icon;

  return (
    <div data-testid="health-score-page" className="min-h-screen bg-gradient-to-b from-muted/30 to-white">
      {/* Hero */}
      <section className="py-16 bg-hero-gradient relative overflow-hidden">
        <div className="decorative-blob w-96 h-96 bg-primary/20 -top-48 -right-48" />
        <div className="max-w-4xl mx-auto px-6 text-center relative">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <span className="badge badge-primary mb-4">
              <Heartbeat weight="fill" className="w-4 h-4 mr-2" />
              Free Assessment
            </span>
            <h1 className="font-display text-4xl md:text-5xl text-foreground mb-4">
              Hospice Health Score
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Get an instant assessment of your organization's operational health across 
              compliance, quality, growth, and leadership dimensions.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Assessment Area */}
      <section className="py-12">
        <div className="max-w-3xl mx-auto px-6">
          <AnimatePresence mode="wait">
            {!showResults ? (
              loading ? (
                <motion.div
                  key="loading"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="text-center py-20"
                >
                  <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
                    <div className="animate-spin w-10 h-10 border-4 border-primary border-t-transparent rounded-full" />
                  </div>
                  <h2 className="font-display text-2xl text-foreground mb-2">Calculating Your Score...</h2>
                  <p className="text-muted-foreground">Analyzing your responses</p>
                </motion.div>
              ) : (
                <motion.div
                  key={`question-${currentQuestion}`}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="bg-white rounded-2xl shadow-lg border border-border p-8"
                >
                  {/* Progress */}
                  <div className="flex items-center justify-between mb-8">
                    <span className="text-sm font-medium text-muted-foreground">
                      Question {currentQuestion + 1} of {questions.length}
                    </span>
                    <span className="badge badge-secondary">{currentQ.category}</span>
                  </div>
                  
                  <div className="w-full bg-muted rounded-full h-2 mb-8">
                    <div 
                      className="bg-primary h-2 rounded-full transition-all duration-300"
                      style={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }}
                    />
                  </div>

                  {/* Question */}
                  <div className="flex items-start gap-4 mb-8">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Icon weight="duotone" className="w-6 h-6 text-primary" />
                    </div>
                    <h2 className="font-display text-xl md:text-2xl text-foreground">
                      {currentQ.question}
                    </h2>
                  </div>

                  {/* Options */}
                  <div className="space-y-3">
                    {currentQ.options.map((option, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleAnswer(option.value)}
                        className="w-full text-left p-4 rounded-xl border-2 border-border hover:border-primary hover:bg-primary/5 transition-all group"
                        data-testid={`option-${idx}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-foreground group-hover:text-primary transition-colors">
                            {option.label}
                          </span>
                          <CaretRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                        </div>
                      </button>
                    ))}
                  </div>
                </motion.div>
              )
            ) : (
              <motion.div
                key="results"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="space-y-8"
              >
                {/* Score Card */}
                <div className="bg-white rounded-2xl shadow-lg border border-border p-8 text-center">
                  <div className={`w-32 h-32 rounded-full ${category.bg} flex items-center justify-center mx-auto mb-6`}>
                    <span className={`font-display text-5xl ${category.color}`}>{score}</span>
                  </div>
                  <div className="flex items-center justify-center gap-2 mb-4">
                    <CategoryIcon weight="fill" className={`w-6 h-6 ${category.color}`} />
                    <span className={`font-semibold text-lg ${category.color}`}>{category.label}</span>
                  </div>
                  <h2 className="font-display text-2xl text-foreground mb-2">Your Hospice Health Score</h2>
                  <p className="text-muted-foreground">
                    Based on your responses across {questions.length} key operational areas
                  </p>
                </div>

                {/* Recommendations */}
                {getRecommendations().length > 0 && (
                  <div className="bg-white rounded-2xl shadow-lg border border-border p-8">
                    <h3 className="font-display text-xl text-foreground mb-6 flex items-center gap-2">
                      <Sparkle weight="fill" className="w-5 h-5 text-accent" />
                      Priority Recommendations
                    </h3>
                    <div className="space-y-4">
                      {getRecommendations().map((rec, idx) => (
                        <div key={idx} className="flex items-start gap-3 p-4 bg-muted/50 rounded-xl">
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <span className="text-sm font-bold text-primary">{idx + 1}</span>
                          </div>
                          <div>
                            <h4 className="font-semibold text-foreground">{rec.area}</h4>
                            <p className="text-sm text-muted-foreground">{rec.text}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Email Capture */}
                {!emailSubmitted ? (
                  <div className="bg-gradient-to-r from-primary to-accent rounded-2xl p-8 text-white">
                    <h3 className="font-display text-xl mb-2">Get Your Full Report</h3>
                    <p className="text-white/80 mb-6">
                      Enter your email to receive a detailed PDF report with personalized action items.
                    </p>
                    <form onSubmit={handleEmailSubmit} className="space-y-4">
                      <input
                        type="text"
                        value={organization}
                        onChange={(e) => setOrganization(e.target.value)}
                        placeholder="Organization Name"
                        className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/50"
                        required
                      />
                      <div className="flex gap-3">
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="your@email.com"
                          className="flex-1 px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/50"
                          required
                        />
                        <button
                          type="submit"
                          className="px-6 py-3 bg-white text-primary font-semibold rounded-xl hover:bg-white/90 transition-colors flex items-center gap-2"
                        >
                          <EnvelopeSimple weight="bold" className="w-5 h-5" />
                          Send
                        </button>
                      </div>
                    </form>
                  </div>
                ) : (
                  <div className="bg-green-50 border border-green-200 rounded-2xl p-8 text-center">
                    <CheckCircle weight="fill" className="w-12 h-12 text-green-500 mx-auto mb-4" />
                    <h3 className="font-display text-xl text-foreground mb-2">Report Sent!</h3>
                    <p className="text-muted-foreground">Check your inbox for your detailed Hospice Health Score report.</p>
                  </div>
                )}

                {/* CTA */}
                <div className="bg-white rounded-2xl shadow-lg border border-border p-8 text-center">
                  <h3 className="font-display text-xl text-foreground mb-2">Ready to Improve Your Score?</h3>
                  <p className="text-muted-foreground mb-6">
                    Schedule a complimentary strategy session to discuss your results and create an action plan.
                  </p>
                  <Link
                    to="/schedule-demo"
                    className="btn-primary inline-flex items-center gap-2"
                    data-testid="schedule-consultation"
                  >
                    Schedule Free Consultation
                    <ArrowRight weight="bold" className="w-5 h-5" />
                  </Link>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>
    </div>
  );
};

export default HealthScorePage;
